import React, { useEffect, useMemo, useState, useImperativeHandle, forwardRef } from 'react';
import { Spin, Pagination, Empty } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import StatsKPIGroup, { KPIItem } from '../Business/StatsKPIGroup';
import { ForgeQueryBuilder, type FieldConfig, type ForgeQuery } from '../ForgeQueryBuilder';
import type { PaginationInfo } from '../../types/base.dto';
import { PAGE_SIZE_OPTIONS } from '../../types/base.dto';

export type PageListRef = {
  reload: () => Promise<void>;
  search: (q: ForgeQuery) => Promise<void>;
  setQuery: (q: ForgeQuery) => void;
  getQuery: () => ForgeQuery;
};

type PageListProps<TRow, TRequest, TStats> = {
  title?: React.ReactNode;
  queryFields: FieldConfig[];
  fetcher: (params: TRequest) => Promise<{ rows: TRow[]; stats?: TStats; pagination?: PaginationInfo }>;
  buildParams?: (q: ForgeQuery) => TRequest;
  defaultQuery?: ForgeQuery;
  kpiItems?: KPIItem[] | ((stats: TStats) => KPIItem[]);
  // Render list content (supports both list and table by passing rows)
  renderList: (props: { rows: TRow[]; loading: boolean }) => React.ReactNode;

  containerStyle?: React.CSSProperties;

  // Extra content before list
  headerExtra?: React.ReactNode;

  // Export callback
  onExport?: (query: ForgeQuery) => void;
  /** 布局配置 */
  layoutConfig?: {
    pagination?: 'fixed' | 'normal';
  };

  /** 是否忽略缓存检索参数 */
  ignoreRemembered?: boolean;

  /** 开启 URL 同步，且支持外部参数并入 URL，此属性改变（如通过 deep equality 检测）会自动同步更新 URL */
  syncToUrl?: boolean | {
    extraParams?: Record<string, string | undefined | null>;
  };
};

const PageList = forwardRef(<TRow extends object, TRequest = any, TStats = any>(props: PageListProps<TRow, TRequest, TStats>, ref: React.Ref<PageListRef>) => {
  const {
    queryFields, fetcher, buildParams, defaultQuery, kpiItems,
    renderList, containerStyle, headerExtra, layoutConfig, onExport,
    ignoreRemembered, syncToUrl
  } = props;

  const navigate = useNavigate();
  const location = useLocation();

  const isPaginationFixed = layoutConfig?.pagination === 'fixed';

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<TRow[]>([]);
  const [stats, setStats] = useState<TStats | undefined>(undefined);
  const [pageInfo, setPageInfo] = useState<PaginationInfo>({ page: 1, page_size: 20, total: 0, total_pages: 0 });
  const [lastQuery, setLastQuery] = useState<ForgeQuery>({});

  const doFetch = async (params: TRequest) => {
    setLoading(true);
    try {
      const result = await fetcher(params);
      setRows((result?.rows || []) as TRow[]);
      setStats(result?.stats);
      if (result?.pagination) setPageInfo(result.pagination);
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    reload: async () => {
      const merged = { ...lastQuery, page: pageInfo.page, pageSize: pageInfo.page_size } as any;
      const params = (buildParams ? buildParams(merged) : (merged as unknown as TRequest));
      await doFetch(params);
    },
    search: async (q: ForgeQuery) => {
      await onSearch(q);
    },
    setQuery: (q: ForgeQuery) => {
      setLastQuery(q);
    },
    getQuery: () => lastQuery
  }));

  useEffect(() => {
    onSearch(defaultQuery || {});
  }, []);

  const extraParamsStr = typeof syncToUrl === 'object' ? JSON.stringify(syncToUrl.extraParams) : '';

  useEffect(() => {
    if (!syncToUrl) return;

    const search = new URLSearchParams(window.location.search);

    // Sync `q`
    search.set('q', JSON.stringify({ filter: lastQuery.filter, sort: lastQuery.sort }));

    // Sync extra params if any
    if (typeof syncToUrl === 'object' && syncToUrl.extraParams) {
      Object.entries(syncToUrl.extraParams).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') {
          search.set(k, v);
        } else {
          search.delete(k);
        }
      });
    }

    navigate(`?${search.toString()}`, { replace: true });
  }, [lastQuery, syncToUrl ? true : false, extraParamsStr, navigate]);

  const kpis = useMemo(() => {
    if (!kpiItems) return undefined;
    return typeof kpiItems === 'function' ? kpiItems(stats as any) : kpiItems;
  }, [kpiItems, stats]);

  const onSearch = async (q: ForgeQuery) => {
    setLastQuery(q);
    const merged = { ...q, page: 1, pageSize: pageInfo.page_size } as any; // Reset to page 1 on search
    const params = (buildParams ? buildParams(merged) : (merged as unknown as TRequest));
    await doFetch(params);
  };

  const handlePageChange = async (page: number, pageSize?: number) => {
    const newPageSize = pageSize || pageInfo.page_size;
    const targetPage = pageSize !== pageInfo.page_size ? 1 : page;

    setPageInfo(prev => ({ ...prev, page: targetPage, page_size: newPageSize }));
    const next = { ...lastQuery, page: targetPage, pageSize: newPageSize } as any;
    const params = (buildParams ? buildParams(next) : (next as unknown as TRequest));
    await doFetch(params);
  };

  return (
    <div style={{ padding: 24, ...containerStyle }}>
      {kpis && kpis.length ? <div style={{ marginBottom: 16 }}><StatsKPIGroup items={kpis as any} /></div> : null}
      <div style={{ marginBottom: 24 }}>
        <ForgeQueryBuilder
          fields={queryFields}
          onSearch={onSearch}
          onExport={onExport}
          defaultQuery={defaultQuery}
          ignoreRemembered={ignoreRemembered}
        />
      </div>
      <div style={{ background: '#fff', padding: 24, borderRadius: 8 }}>
        {headerExtra}

        {/* List Content */}
        <div style={{ marginTop: 24, marginBottom: isPaginationFixed ? 64 : 0 }}>
          <Spin spinning={loading}>
            {rows.length > 0 ? (
              renderList({ rows, loading })
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无数据" />
            )}
          </Spin>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: 24,
          position: isPaginationFixed ? 'fixed' : 'sticky',
          bottom: 0,
          left: isPaginationFixed ? 0 : undefined,
          right: isPaginationFixed ? 0 : undefined,
          background: '#fff',
          padding: '16px 0',
          zIndex: 10,
          borderTop: '1px solid #f0f0f0',
          boxShadow: isPaginationFixed ? '0 -2px 8px rgba(0,0,0,0.08)' : undefined,
        }}>
          <Pagination
            current={pageInfo.page}
            pageSize={pageInfo.page_size}
            total={pageInfo.total}
            showSizeChanger
            showQuickJumper
            showTotal={(total) => `共 ${total} 条记录`}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onChange={handlePageChange}
          />
        </div>
      </div>
    </div>
  );
}) as <TRow extends object, TRequest = any, TStats = any>(props: PageListProps<TRow, TRequest, TStats> & { ref?: React.Ref<PageListRef> }) => React.ReactElement;

export default PageList;
